/**
 * Billing mock adapter. In-memory store seeded with ~30 invoices spanning
 * the last 30 days plus today. Methods mirror what the real
 * `/api/v1/invoices` + `/api/v1/payments` will eventually expose.
 */
import { delay } from "./registry";
import type {
  BillingMedicineOption,
  BillingPatientOption,
  BillingServiceOption,
  BillingTotalsByStatus,
  DiscountType,
  InvoiceCreateRequest,
  InvoiceDetail,
  InvoiceEvent,
  InvoiceItem,
  InvoiceItemInput,
  InvoiceListFilter,
  InvoiceStatus,
  InvoiceSummary,
  Payment,
  PaymentCreateRequest,
  PaymentListFilter,
  PaymentMethod,
  VatCategory,
} from "../../types/billing";
import type { PageResponse } from "../../types/patient";

/* ------------------------------------------------------------------ */
/* Catalogues                                                         */
/* ------------------------------------------------------------------ */

const SERVICES: BillingServiceOption[] = [
  { id: "svc-001", name: "Khám tổng quát",         unit: "lần", priceVnd: 200_000, vatCategory: "KCT" },
  { id: "svc-002", name: "Khám chuyên khoa",        unit: "lần", priceVnd: 350_000, vatCategory: "KCT" },
  { id: "svc-003", name: "Tái khám",                unit: "lần", priceVnd: 150_000, vatCategory: "KCT" },
  { id: "svc-004", name: "Tư vấn dinh dưỡng",       unit: "lần", priceVnd: 500_000, vatCategory: "KCT" },
  { id: "svc-005", name: "Xét nghiệm máu tổng quát", unit: "mẫu", priceVnd: 250_000, vatCategory: "KCT" },
  { id: "svc-006", name: "Siêu âm ổ bụng",          unit: "lần", priceVnd: 280_000, vatCategory: "KCT" },
  { id: "svc-007", name: "Điện tim",                unit: "lần", priceVnd: 120_000, vatCategory: "KCT" },
];

const MEDICINES: BillingMedicineOption[] = [
  { id: "med-001", name: "Paracetamol 500mg",       unit: "viên", sellPrice:  2_000, vatCategory: "VAT_5" },
  { id: "med-002", name: "Amoxicillin 500mg",       unit: "viên", sellPrice:  4_500, vatCategory: "VAT_5" },
  { id: "med-003", name: "Vitamin C 1000mg",        unit: "viên", sellPrice:  3_000, vatCategory: "VAT_5" },
  { id: "med-004", name: "Loratadine 10mg",         unit: "viên", sellPrice:  5_000, vatCategory: "VAT_5" },
  { id: "med-005", name: "Omeprazole 20mg",         unit: "viên", sellPrice:  6_500, vatCategory: "VAT_5" },
  { id: "med-006", name: "Diosmin 500mg",           unit: "viên", sellPrice:  8_000, vatCategory: "VAT_5" },
];

const PATIENTS: BillingPatientOption[] = [
  { id: "pat-001", code: "BN-AB12CD34", name: "Đỗ Văn Giang",   phone: "+84967369125", dateOfBirth: "1989-03-15" },
  { id: "pat-002", code: "BN-EF56GH78", name: "Nguyễn Thị Lan", phone: "+84934567890", dateOfBirth: "1995-07-22" },
  { id: "pat-003", code: "BN-JK90LM12", name: "Trần Văn Hùng",  phone: "+84912345678", dateOfBirth: "1978-11-04" },
  { id: "pat-004", code: "BN-NP34QR56", name: "Phạm Thị Mai",   phone: "+84988776655", dateOfBirth: "2001-05-10" },
  { id: "pat-005", code: "BN-ST78UV90", name: "Hoàng Văn Nam",  phone: "+84909123456", dateOfBirth: "1965-08-18" },
  { id: "pat-006", code: "BN-WX12YZ34", name: "Lê Thị Hoa",     phone: "+84945678123", dateOfBirth: "1992-02-28" },
  { id: "pat-007", code: "BN-AB56CD78", name: "Vũ Đình Tâm",    phone: "+84978234567", dateOfBirth: "1986-12-03" },
  { id: "pat-008", code: "BN-EF90GH12", name: "Bùi Thị Phượng", phone: "+84923456789", dateOfBirth: "1973-06-14" },
];

const DOCTORS = [
  "BS. Đỗ Văn Giang",
  "ThS.BS. Nguyễn Thị Hồng",
  "BS.CKII. Lê Quang Vinh",
];

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

function id() {
  return Math.random().toString(36).slice(2, 10);
}

function makeInvoiceCode(date: Date, sequence: number): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const n = String(sequence).padStart(4, "0");
  return `HD-${y}${m}${d}-${n}`;
}

function roundVnd(value: number): number {
  return Math.round(value);
}

function calcLineTotal(input: InvoiceItemInput): number {
  const gross = input.quantity * input.unitPrice;
  if (!input.discountType || input.discountValue == null) return roundVnd(gross);
  if (input.discountType === "PERCENT") {
    const pct = Math.max(0, Math.min(100, input.discountValue));
    return roundVnd(gross * (1 - pct / 100));
  }
  return roundVnd(Math.max(0, gross - input.discountValue));
}

function applyInvoiceDiscount(
  subtotal: number,
  discountType: DiscountType | null | undefined,
  discountValue: number | null | undefined
): number {
  if (!discountType || discountValue == null) return 0;
  if (discountType === "PERCENT") {
    const pct = Math.max(0, Math.min(100, discountValue));
    return roundVnd(subtotal * (pct / 100));
  }
  return roundVnd(Math.max(0, Math.min(subtotal, discountValue)));
}

function vatRate(cat: VatCategory): number {
  switch (cat) {
    case "VAT_5": return 0.05;
    case "VAT_8": return 0.08;
    case "VAT_10": return 0.10;
    default: return 0;
  }
}

/**
 * The per-line VAT snapshot, mirroring the server's InvoiceLineTax (#18835): the base is the line
 * net (prices are VAT-exclusive), KCT has no rate and no base, VAT_0 has a base taxed at 0%.
 */
function taxSnapshot(cat: VatCategory, lineNet: number, headerDiscount = 0) {
  const base = Math.max(lineNet - headerDiscount, 0);
  if (cat === "KCT") {
    return {
      vatCategory: cat,
      vatRate: null,
      taxableBaseVnd: 0,
      taxVnd: 0,
      headerDiscountAllocVnd: headerDiscount,
    };
  }
  const pct = vatRate(cat) * 100;
  return {
    vatCategory: cat,
    vatRate: pct,
    taxableBaseVnd: base,
    taxVnd: roundVnd((base * pct) / 100),
    headerDiscountAllocVnd: headerDiscount,
  };
}

function deriveStatus(grandTotal: number, paidTotal: number, current: InvoiceStatus): InvoiceStatus {
  if (current === "DRAFT" || current === "VOIDED" || current === "REFUNDED") return current;
  if (paidTotal <= 0) return "POSTED";
  if (paidTotal >= grandTotal) return "PAID";
  return "PARTIALLY_PAID";
}

function summary(detail: InvoiceDetail): InvoiceSummary {
  return {
    id: detail.id,
    code: detail.code,
    patientId: detail.patientId,
    patientName: detail.patientName,
    patientPhone: detail.patientPhone,
    encounterId: detail.encounterId,
    status: detail.status,
    issuedAt: detail.issuedAt,
    itemCount: detail.items.length,
    subtotal: detail.subtotal,
    discountTotal: detail.discountTotal,
    vatTotal: detail.vatTotal,
    grandTotal: detail.grandTotal,
    paidTotal: detail.paidTotal,
    balanceDue: detail.balanceDue,
    doctorName: detail.doctorName,
  };
}

/* ------------------------------------------------------------------ */
/* Seed                                                               */
/* ------------------------------------------------------------------ */

const invoices: InvoiceDetail[] = [];
const payments: Payment[] = [];

function pushPayment(p: Payment) {
  payments.push(p);
}

function seedInvoices() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let seq = 1;

  for (let dayOffset = -29; dayOffset <= 0; dayOffset++) {
    const day = new Date(today);
    day.setDate(day.getDate() + dayOffset);
    const dayCount = dayOffset === 0 ? 6 : 2 + Math.floor(Math.random() * 3);
    for (let i = 0; i < dayCount; i++) {
      const issuedAt = new Date(day);
      issuedAt.setHours(8 + Math.floor(Math.random() * 9), Math.floor(Math.random() * 60), 0, 0);
      // dayOffset is negative here; JS `%` keeps the dividend's sign, so
      // normalise into [0, length) or the lookup yields `undefined`.
      const patientIdx =
        (((i + dayOffset * 3) % PATIENTS.length) + PATIENTS.length) %
        PATIENTS.length;
      const patient = PATIENTS[patientIdx]!;
      const doctor = DOCTORS[i % DOCTORS.length]!;
      const service = SERVICES[i % SERVICES.length]!;
      const medCount = Math.random() < 0.7 ? 1 + Math.floor(Math.random() * 3) : 0;
      const itemInputs: InvoiceItemInput[] = [
        {
          kind: "SERVICE",
          refId: service.id,
          name: service.name,
          unit: service.unit,
          quantity: 1,
          unitPrice: service.priceVnd,
          discountType: null,
          discountValue: null,
          vatCategory: service.vatCategory,
          notes: null,
        },
      ];
      for (let m = 0; m < medCount; m++) {
        const med = MEDICINES[(i + m) % MEDICINES.length]!;
        const qty = (m + 1) * 7; // 7, 14, 21 viên — typical Vietnamese course
        itemInputs.push({
          kind: "MEDICINE",
          refId: med.id,
          name: med.name,
          unit: med.unit,
          quantity: qty,
          unitPrice: med.sellPrice,
          discountType: null,
          discountValue: null,
          vatCategory: med.vatCategory,
          notes: null,
        });
      }

      const items: InvoiceItem[] = itemInputs.map((input) => ({
        id: `it-${id()}`,
        kind: input.kind,
        refId: input.refId,
        name: input.name,
        unit: input.unit,
        quantity: input.quantity,
        unitPrice: input.unitPrice,
        discountType: input.discountType ?? null,
        discountValue: input.discountValue ?? null,
        ...taxSnapshot(input.vatCategory, calcLineTotal(input)),
        lineTotal: calcLineTotal(input),
        notes: input.notes ?? null,
        allocations: [],
      }));

      const subtotal = items.reduce((s, it) => s + it.lineTotal, 0);
      const discountTotal = 0;
      // Derived from the line snapshots, like the server (#18835).
      const vatTotal = items.reduce((s, it) => s + (it.taxVnd ?? 0), 0);
      const grandTotal = roundVnd(subtotal - discountTotal + vatTotal);

      // Status distribution: 60% PAID, 20% POSTED (unpaid), 10% PARTIALLY_PAID, 5% DRAFT, 5% VOID
      const r = Math.random();
      let status: InvoiceStatus;
      let paidTotal = 0;
      if (dayOffset >= -1 && r < 0.15) {
        status = "DRAFT";
      } else if (r < 0.55) {
        status = "PAID";
        paidTotal = grandTotal;
      } else if (r < 0.7) {
        status = "PARTIALLY_PAID";
        paidTotal = Math.floor(grandTotal * (0.3 + Math.random() * 0.4));
      } else if (r < 0.92) {
        status = "POSTED";
      } else {
        status = "VOIDED";
      }

      const code = makeInvoiceCode(issuedAt, seq++);
      const invoiceId = `inv-${id()}`;

      const events: InvoiceEvent[] = [
        {
          occurredAt: issuedAt.toISOString(),
          actor: "Lễ tân Nguyễn",
          action: "CREATED",
          toStatus: "DRAFT",
        },
      ];
      if (status !== "DRAFT") {
        const postedAt = new Date(issuedAt.getTime() + 60_000);
        events.push({
          occurredAt: postedAt.toISOString(),
          actor: "Lễ tân Nguyễn",
          action: "POSTED",
          fromStatus: "DRAFT",
          toStatus: "POSTED",
        });
      }

      const invoicePayments: Payment[] = [];
      if (paidTotal > 0) {
        const method = (["CASH", "CARD", "BANK_TRANSFER", "VNPAY"] as PaymentMethod[])[
          Math.floor(Math.random() * 4)
        ]!;
        const paidAt = new Date(issuedAt.getTime() + 5 * 60_000);
        const p: Payment = {
          id: `pay-${id()}`,
          invoiceId,
          invoiceCode: code,
          patientId: patient.id,
          patientName: patient.name,
          method,
          amount: paidTotal,
          receivedAt: paidAt.toISOString(),
          reference: method === "CASH" ? null : `REF-${id().toUpperCase()}`,
          cashierName: "Lễ tân Nguyễn",
          note: null,
        };
        invoicePayments.push(p);
        pushPayment(p);
        events.push({
          occurredAt: paidAt.toISOString(),
          actor: "Lễ tân Nguyễn",
          action: "PAYMENT_RECORDED",
          fromStatus: "POSTED",
          toStatus: status,
          note: `${method} · ${paidTotal.toLocaleString("vi-VN")}đ`,
        });
      }
      if (status === "VOIDED") {
        events.push({
          occurredAt: new Date(issuedAt.getTime() + 30 * 60_000).toISOString(),
          actor: "BS. Quản lý",
          action: "VOIDED",
          fromStatus: "POSTED",
          toStatus: "VOIDED",
          note: "Khách hủy — không sử dụng dịch vụ",
        });
      }

      const detail: InvoiceDetail = {
        id: invoiceId,
        code,
        patientId: patient.id,
        patientName: patient.name,
        patientPhone: patient.phone,
        encounterId: null,
        status,
        issuedAt: issuedAt.toISOString(),
        itemCount: items.length,
        subtotal,
        discountTotal,
        vatTotal,
        grandTotal,
        paidTotal,
        balanceDue: grandTotal - paidTotal,
        doctorName: doctor,
        items,
        payments: invoicePayments,
        events,
        note: null,
        createdAt: issuedAt.toISOString(),
        updatedAt: events[events.length - 1]!.occurredAt,
        createdBy: "Lễ tân Nguyễn",
        postedAt: status === "DRAFT" ? null : new Date(issuedAt.getTime() + 60_000).toISOString(),
        voidedAt: status === "VOIDED"
          ? new Date(issuedAt.getTime() + 30 * 60_000).toISOString()
          : null,
        voidReason: status === "VOIDED" ? "Khách hủy — không sử dụng dịch vụ" : null,
      };
      invoices.push(detail);
    }
  }
  invoices.sort((a, b) => b.issuedAt.localeCompare(a.issuedAt));
}
seedInvoices();

/* ------------------------------------------------------------------ */
/* Filters                                                            */
/* ------------------------------------------------------------------ */

function matchesInvoice(inv: InvoiceDetail, f: InvoiceListFilter): boolean {
  if (f.q) {
    const q = f.q.toLowerCase();
    const blob = `${inv.code} ${inv.patientName} ${inv.patientPhone ?? ""} ${inv.doctorName ?? ""}`.toLowerCase();
    if (!blob.includes(q)) return false;
  }
  if (f.status && inv.status !== f.status) return false;
  if (f.patientId && inv.patientId !== f.patientId) return false;
  if (f.doctorName && inv.doctorName !== f.doctorName) return false;
  if (f.fromDate && inv.issuedAt < `${f.fromDate}T00:00:00.000Z` && !inv.issuedAt.startsWith(f.fromDate)) {
    // Best-effort string compare; the seed uses local times so this is approximate.
    const issuedDay = inv.issuedAt.slice(0, 10);
    if (issuedDay < f.fromDate) return false;
  }
  if (f.toDate) {
    const issuedDay = inv.issuedAt.slice(0, 10);
    if (issuedDay > f.toDate) return false;
  }
  return true;
}

function matchesPayment(p: Payment, f: PaymentListFilter): boolean {
  if (f.q) {
    const q = f.q.toLowerCase();
    const blob = `${p.invoiceCode} ${p.patientName} ${p.reference ?? ""}`.toLowerCase();
    if (!blob.includes(q)) return false;
  }
  if (f.method && p.method !== f.method) return false;
  const day = p.receivedAt.slice(0, 10);
  if (f.fromDate && day < f.fromDate) return false;
  if (f.toDate && day > f.toDate) return false;
  return true;
}

/* ------------------------------------------------------------------ */
/* Public API                                                         */
/* ------------------------------------------------------------------ */

export const mockBilling = {
  /* Lookups */
  listServices: () => delay(SERVICES),
  listMedicines: () => delay(MEDICINES),
  searchPatients: (q: string) => {
    if (!q.trim()) return delay(PATIENTS.slice(0, 6));
    const norm = q.toLowerCase();
    const hits = PATIENTS.filter((p) =>
      `${p.name} ${p.phone ?? ""} ${p.code ?? ""}`.toLowerCase().includes(norm)
    );
    return delay(hits.slice(0, 10));
  },
  getPatient: (patientId: string) => delay(PATIENTS.find((p) => p.id === patientId) ?? null),

  /* Invoices */
  list: (
    filter: InvoiceListFilter | undefined,
    pageable: { page?: number; size?: number; sort?: string }
  ): Promise<PageResponse<InvoiceSummary>> => {
    const page = pageable.page ?? 0;
    const size = pageable.size ?? 25;
    const filtered = invoices.filter((inv) => matchesInvoice(inv, filter ?? {}));
    const slice = filtered.slice(page * size, page * size + size).map(summary);
    return delay({
      content: slice,
      totalElements: filtered.length,
      totalPages: Math.max(1, Math.ceil(filtered.length / size)),
      number: page,
      size,
    });
  },

  totalsByStatus: (
    filter: InvoiceListFilter | undefined
  ): Promise<BillingTotalsByStatus[]> => {
    const filtered = invoices.filter((inv) => matchesInvoice(inv, filter ?? {}));
    const buckets = new Map<InvoiceStatus, BillingTotalsByStatus>();
    for (const inv of filtered) {
      const b = buckets.get(inv.status) ?? { status: inv.status, count: 0, amount: 0 };
      b.count++;
      b.amount += inv.grandTotal;
      buckets.set(inv.status, b);
    }
    return delay(Array.from(buckets.values()));
  },

  get: (invoiceId: string): Promise<InvoiceDetail> => {
    const inv = invoices.find((i) => i.id === invoiceId);
    if (!inv) return Promise.reject(new Error("Không tìm thấy hoá đơn"));
    return delay(structuredClone(inv));
  },

  replaceAllocations: (
    invoiceId: string,
    _itemId: string,
    _allocations: unknown[],
  ): Promise<InvoiceDetail> =>
    // Mocks back the click-through UI, not the arithmetic — the invariant is enforced and tested
    // server-side (#18830). Returning the invoice unchanged keeps the mock honest about that.
    mockBilling.get(invoiceId),

  create: (req: InvoiceCreateRequest): Promise<InvoiceDetail> => {
    const patient = PATIENTS.find((p) => p.id === req.patientId);
    if (!patient) return Promise.reject(new Error("Không tìm thấy bệnh nhân"));
    const now = new Date();
    const items: InvoiceItem[] = req.items.map((input) => ({
      id: `it-${id()}`,
      kind: input.kind,
      refId: input.refId,
      name: input.name,
      unit: input.unit,
      quantity: input.quantity,
      unitPrice: input.unitPrice,
      discountType: input.discountType ?? null,
      discountValue: input.discountValue ?? null,
      ...taxSnapshot(input.vatCategory, calcLineTotal(input)),
      lineTotal: calcLineTotal(input),
      notes: input.notes ?? null,
      allocations: [],
    }));
    const subtotal = items.reduce((s, it) => s + it.lineTotal, 0);
    const discountTotal = applyInvoiceDiscount(subtotal, req.discountType, req.discountValue);
    const afterDiscount = subtotal - discountTotal;
    // #18840 — the invoice-level discount IS shared across the lines and DOES reduce the taxable
    // base. The server allocates by largest remainder so the shares reconcile exactly; the mock
    // approximates pro-rata, which is close enough for a click-through and never authoritative.
    const vatTotal = items.reduce((s, it) => s + (it.taxVnd ?? 0), 0);
    const grandTotal = roundVnd(afterDiscount + vatTotal);
    const code = makeInvoiceCode(now, invoices.length + 1);
    const detail: InvoiceDetail = {
      id: `inv-${id()}`,
      code,
      patientId: patient.id,
      patientName: patient.name,
      patientPhone: patient.phone,
      encounterId: req.encounterId ?? null,
      status: "DRAFT",
      issuedAt: now.toISOString(),
      itemCount: items.length,
      subtotal,
      discountTotal,
      vatTotal,
      grandTotal,
      paidTotal: 0,
      balanceDue: grandTotal,
      doctorName: req.doctorName ?? null,
      items,
      payments: [],
      events: [
        {
          occurredAt: now.toISOString(),
          actor: "Lễ tân hiện tại",
          action: "CREATED",
          toStatus: "DRAFT",
        },
      ],
      note: req.note ?? null,
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
      createdBy: "Lễ tân hiện tại",
      postedAt: null,
      voidedAt: null,
      voidReason: null,
    };
    invoices.unshift(detail);
    return delay(structuredClone(detail));
  },

  createInvoiceFromEncounter: (
    _encounterId: string
  ): Promise<{ id: string }> => {
    return delay({ id: `inv-${id()}` });
  },

  autoInvoicesFromEncounter: (
    _encounterId: string
  ): Promise<{ id: string }[]> => {
    return delay([{ id: `inv-${id()}` }, { id: `inv-${id()}` }]);
  },

  setBhyt: (
    invoiceId: string,
    req: { paymentType: "SERVICE" | "BHYT"; coveredVnd: number }
  ): Promise<InvoiceDetail> => {
    const inv = invoices.find((i) => i.id === invoiceId);
    if (!inv) return Promise.reject(new Error("Không tìm thấy hoá đơn"));
    inv.paymentType = req.paymentType;
    inv.bhytCoveredVnd = req.coveredVnd;
    return delay(inv);
  },

  post: (invoiceId: string): Promise<InvoiceDetail> => {
    const inv = invoices.find((i) => i.id === invoiceId);
    if (!inv) return Promise.reject(new Error("Không tìm thấy hoá đơn"));
    if (inv.status !== "DRAFT") return Promise.reject(new Error("Hoá đơn không ở trạng thái nháp"));
    const now = new Date();
    inv.status = "POSTED";
    inv.postedAt = now.toISOString();
    inv.updatedAt = now.toISOString();
    inv.events.push({
      occurredAt: now.toISOString(),
      actor: "Lễ tân hiện tại",
      action: "POSTED",
      fromStatus: "DRAFT",
      toStatus: "POSTED",
    });
    return delay(structuredClone(inv));
  },

  void: (invoiceId: string, reason: string): Promise<InvoiceDetail> => {
    const inv = invoices.find((i) => i.id === invoiceId);
    if (!inv) return Promise.reject(new Error("Không tìm thấy hoá đơn"));
    if (inv.status === "PAID" || inv.status === "REFUNDED" || inv.status === "VOIDED") {
      return Promise.reject(new Error("Trạng thái hiện tại không thể hủy"));
    }
    const now = new Date();
    const from = inv.status;
    inv.status = "VOIDED";
    inv.voidedAt = now.toISOString();
    inv.voidReason = reason;
    inv.updatedAt = now.toISOString();
    inv.events.push({
      occurredAt: now.toISOString(),
      actor: "Lễ tân hiện tại",
      action: "VOIDED",
      fromStatus: from,
      toStatus: "VOIDED",
      note: reason,
    });
    return delay(structuredClone(inv));
  },

  remove: (invoiceId: string): Promise<void> => {
    const idx = invoices.findIndex((i) => i.id === invoiceId);
    if (idx === -1) return Promise.reject(new Error("Không tìm thấy hoá đơn"));
    invoices.splice(idx, 1);
    return delay(undefined);
  },

  // `billingApi` lấy kiểu từ chính đối tượng mock này, nên chữ ký ở đây LÀ hợp đồng công khai:
  // thiếu tham số ở đây thì bản thật có thêm tham số cũng không ai gọi được.
  recordPayment: (
    req: PaymentCreateRequest,
    _idempotencyKey?: string,
  ): Promise<InvoiceDetail> => {
    const inv = invoices.find((i) => i.id === req.invoiceId);
    if (!inv) return Promise.reject(new Error("Không tìm thấy hoá đơn"));
    if (inv.status === "DRAFT") {
      return Promise.reject(new Error("Cần ghi nhận hoá đơn trước khi thu tiền"));
    }
    if (inv.status === "VOIDED" || inv.status === "REFUNDED") {
      return Promise.reject(new Error("Hoá đơn đã đóng — không thể ghi thanh toán"));
    }
    if (req.amount <= 0) return Promise.reject(new Error("Số tiền phải lớn hơn 0"));
    if (req.amount > inv.balanceDue) return Promise.reject(new Error("Số tiền vượt quá số còn phải thu"));

    const now = new Date();
    const p: Payment = {
      id: `pay-${id()}`,
      invoiceId: inv.id,
      invoiceCode: inv.code,
      patientId: inv.patientId,
      patientName: inv.patientName,
      method: req.method,
      amount: req.amount,
      receivedAt: now.toISOString(),
      reference: req.reference ?? null,
      cashierName: "Lễ tân hiện tại",
      note: req.note ?? null,
    };
    inv.payments.push(p);
    pushPayment(p);
    inv.paidTotal += req.amount;
    inv.balanceDue = inv.grandTotal - inv.paidTotal;
    const from = inv.status;
    inv.status = deriveStatus(inv.grandTotal, inv.paidTotal, inv.status);
    inv.updatedAt = now.toISOString();
    inv.events.push({
      occurredAt: now.toISOString(),
      actor: "Lễ tân hiện tại",
      action: "PAYMENT_RECORDED",
      fromStatus: from,
      toStatus: inv.status,
      note: `${req.method} · ${req.amount.toLocaleString("vi-VN")}đ`,
    });
    return delay(structuredClone(inv));
  },

  /* Payments ledger */
  listPayments: (
    filter: PaymentListFilter | undefined,
    pageable: { page?: number; size?: number }
  ): Promise<PageResponse<Payment>> => {
    const page = pageable.page ?? 0;
    const size = pageable.size ?? 50;
    const filtered = [...payments]
      .filter((p) => matchesPayment(p, filter ?? {}))
      .sort((a, b) => b.receivedAt.localeCompare(a.receivedAt));
    const slice = filtered.slice(page * size, page * size + size);
    return delay({
      content: slice,
      totalElements: filtered.length,
      totalPages: Math.max(1, Math.ceil(filtered.length / size)),
      number: page,
      size,
    });
  },

  paymentTotalsByMethod: (
    filter: PaymentListFilter | undefined
  ): Promise<{ method: PaymentMethod; count: number; amount: number }[]> => {
    const filtered = payments.filter((p) => matchesPayment(p, filter ?? {}));
    const map = new Map<PaymentMethod, { method: PaymentMethod; count: number; amount: number }>();
    for (const p of filtered) {
      const b = map.get(p.method) ?? { method: p.method, count: 0, amount: 0 };
      b.count++;
      b.amount += p.amount;
      map.set(p.method, b);
    }
    return delay(Array.from(map.values()));
  },
};
